import { useEffect, useState } from "react";
import { Check, FileText, Loader2, PhoneIncoming, PhoneOutgoing, RefreshCw, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useFetch } from "@/hooks/useFetch";
import { fmtDate } from "@/lib/time";
import { fmtDur } from "@/components/sales/constants";

const TranscriptDialog = ({ id, onClose }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (id) api.get(`/sales/calls/${id}/detail`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => { if (!o) { setD(null); setErr(""); onClose(); } }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" aria-describedby={undefined} data-testid="transcript-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Call details</DialogTitle></DialogHeader>
        {!d && !err && <Loader2 className="h-5 w-5 animate-spin" />}
        {err && <p className="text-sm text-red-300">{err}</p>}
        {d && (
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">{fmtDate(d.call.started_at)} · {d.call.direction} · {fmtDur(d.call.duration_seconds)} · ext {d.call.agent_display_name}</p>
            <p>{d.analysis.summary}</p>
            {d.analysis.next_steps && <p><b>Next steps:</b> {d.analysis.next_steps}</p>}
            {d.analysis.kpi_scores && <p className="flex flex-wrap gap-2">{Object.entries(d.analysis.kpi_scores).map(([k, v]) => <span key={k} className="rounded-full bg-white/5 px-2 py-0.5 text-xs">{k.replace(/_/g, " ")}: {v}</span>)}</p>}
            <pre className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded-xl bg-background/60 p-4 font-sans text-sm" data-testid="transcript-text">{d.transcript || "No transcript available."}</pre>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CallRow = ({ c, company, possible }) => {
  const { run } = useApp();
  const [view, setView] = useState(null);
  const [contact, setContact] = useState(c.contact_id || "");
  const act = (action) => run(() => api.post(`/sales/calls/${c.id}/match`, { company_id: company.id, action, contact_id: contact || null }), action === "confirm" ? "Call linked" : "Not this company");
  const Icon = c.direction === "inbound" ? PhoneIncoming : PhoneOutgoing;
  const cand = possible && c.candidates?.find((x) => x.company_id === company.id);
  return (
    <div className="border-b py-3 last:border-0" data-testid={`insight-call-${c.id}`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {fmtDate(c.started_at)} · {fmtDur(c.duration_seconds)} · {c.sentiment || "no sentiment"}
        <span className="ml-auto rounded-full bg-white/5 px-2 py-0.5">{c.match_status === "confirmed" ? "Confirmed" : c.match_status === "strong" ? "Number match" : "Possible match"}</span>
      </div>
      <p className="mt-1 text-sm">{c.summary || "No summary yet."}</p>
      {c.next_steps && <p className="mt-1 text-xs text-cyan-200">Next: {c.next_steps}</p>}
      {cand && <p className="mt-1 text-xs text-amber-300">{cand.evidence}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button onClick={() => setView(c.id)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid={`view-transcript-${c.id}`}><FileText className="h-3.5 w-3.5" /> Transcript</button>
        {(possible || (c.match_status === "strong" && !c.contact_id)) && (company.contacts?.length > 0) && (
          <select value={contact} onChange={(e) => setContact(e.target.value)} className="h-7 rounded-full border bg-card px-2 text-xs" data-testid={`call-contact-select-${c.id}`}>
            <option value="">Who was it?</option>{company.contacts.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        )}
        {c.match_status !== "confirmed" && <button onClick={() => act("confirm")} className="flex items-center gap-1 rounded-full border border-emerald-500/40 px-2 py-0.5 text-xs text-emerald-300" data-testid={`confirm-call-${c.id}`}><Check className="h-3 w-3" /> Confirm</button>}
        <button onClick={() => act("reject")} className="flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-muted-foreground hover:text-red-300" data-testid={`reject-call-${c.id}`}><X className="h-3 w-3" /> Not this company</button>
      </div>
      <TranscriptDialog id={view} onClose={() => setView(null)} />
    </div>
  );
};

export const CallsPanel = ({ company }) => {
  const [force, setForce] = useState(0);
  const d = useFetch(`/sales/companies/${company.id}/calls`, force ? { refresh: true, n: force } : {});
  return (
    <section className="rounded-2xl border bg-card/70 p-5" data-testid="calls-panel">
      <div className="mb-2 flex items-center justify-between"><h2 className="font-display text-lg font-semibold">Atom Insight calls</h2>
        <button onClick={() => setForce((n) => n + 1)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="refresh-calls"><RefreshCw className="inline h-3 w-3" /> Refresh</button></div>
      {!d ? <div className="h-16 animate-pulse rounded-xl bg-white/[0.04]" /> : (
        <>
          {d.error && <p className="mb-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="calls-error">Atom Insight is unavailable right now ({d.error}). Showing calls we already have.</p>}
          {d.matched.length === 0 && d.possible.length === 0 && <p className="text-sm text-muted-foreground">No Insight calls found for this company's numbers in the last 90 days.</p>}
          {d.matched.map((c) => <CallRow key={c.id} c={c} company={company} />)}
          {d.possible.length > 0 && <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-amber-300">Possible matches — please check</p>}
          {d.possible.map((c) => <CallRow key={c.id} c={c} company={company} possible />)}
        </>
      )}
    </section>
  );
};
